import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import { useVenue } from '../../context/VenueContext';
import { Plus, ToggleLeft, ToggleRight, RefreshCw, X, Trash2, Pencil, MapPin, Check } from 'lucide-react';

const ROLES = {
  cashier: 'Cassiere',
  supervisor: 'Supervisore',
  admin: 'Admin',
  brand_admin: 'Admin Brand',
  venue_admin: 'Admin PV',
};
const ROLE_COLORS = {
  cashier: 'bg-blue-50 text-blue-600',
  supervisor: 'bg-purple-50 text-purple-600',
  admin: 'bg-orange-50 text-[#D44E20]',
  brand_admin: 'bg-orange-50 text-[#D44E20]',
  venue_admin: 'bg-amber-50 text-amber-600',
};

export default function AdminStaff() {
  const venueCtx = useVenue();
  const venues = venueCtx?.venues || [];
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', role: 'cashier', pin: '', venue_ids: [] });
  const [saving, setSaving] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [newPin, setNewPin] = useState('');

  const fetchStaff = () => { adminApi.get('/staff').then(r => setStaff(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { fetchStaff(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ name: '', role: 'cashier', pin: '', venue_ids: venues.map(v => v.id) });
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      role: s.role,
      pin: '',
      venue_ids: s.venue_ids || venues.map(v => v.id),
    });
    setShowForm(true);
  };

  const toggleVenue = (vid) => {
    setForm(f => ({
      ...f,
      venue_ids: f.venue_ids.includes(vid)
        ? f.venue_ids.filter(v => v !== vid)
        : [...f.venue_ids, vid],
    }));
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Nome obbligatorio'); return; }
    if (!editingId && (!form.pin || form.pin.length !== 6)) { toast.error('PIN di 6 cifre obbligatorio'); return; }
    if (form.venue_ids.length === 0) { toast.error('Seleziona almeno un punto vendita'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, role: form.role, venue_ids: form.venue_ids };
      if (editingId) {
        if (form.pin && form.pin.length === 6) payload.pin = form.pin;
        await adminApi.put(`/staff/${editingId}`, payload);
        toast.success('Operatore aggiornato');
      } else {
        payload.pin = form.pin;
        await adminApi.post('/staff', payload);
        toast.success('Operatore aggiunto');
      }
      setShowForm(false);
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (s) => {
    await adminApi.put(`/staff/${s.id}`, { is_active: !s.is_active });
    toast.success(s.is_active ? 'Disattivato' : 'Riattivato');
    fetchStaff();
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Eliminare "${s.name}"? Irreversibile.`)) return;
    try { await adminApi.delete(`/staff/${s.id}`); toast.success('Eliminato'); fetchStaff(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const getVenueNames = (vids) => {
    if (!vids || vids.length === 0 || vids.length === venues.length) return 'Tutti i PV';
    return vids.map(vid => venues.find(v => v.id === vid)?.name || vid).join(', ');
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-staff-page">
      <PageTooltip page="staff" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Staff</h1><p className="text-[#64748B] text-sm">{staff.length} operatori</p></div>
        <button onClick={openAdd} data-testid="add-staff-btn" className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D44E20] text-white rounded-xl text-sm font-semibold hover:bg-[#B83D15] transition-colors">
          <Plus size={16} /> Aggiungi Operatore
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div> : (
        <div className="space-y-3">
          {staff.map(s => (
            <div key={s.id} data-testid={`staff-item-${s.id}`} className={`bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] ${!s.is_active ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#FDECE7] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D44E20] font-bold">{s.name?.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1A1A1A] font-semibold text-sm">{s.name}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[s.role] || 'bg-slate-50 text-[#64748B]'}`}>{ROLES[s.role] || s.role}</span>
                    {venues.length > 1 && (
                      <span className="text-xs text-[#64748B] flex items-center gap-1">
                        <MapPin size={10} /> {getVenueNames(s.venue_ids)}
                      </span>
                    )}
                    {s.email && <span className="text-[#94A3B8] text-xs">{s.email}</span>}
                  </div>
                  {s.last_login && <p className="text-[#94A3B8] text-[10px] mt-0.5">Ultimo: {new Date(s.last_login).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-[#64748B] hover:bg-[#FDECE7] hover:text-[#D44E20] transition-colors">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleToggle(s)} className="flex-shrink-0">
                    {s.is_active ? <ToggleRight size={22} className="text-[#10B981]" /> : <ToggleLeft size={22} className="text-[#64748B]" />}
                  </button>
                  <button onClick={() => handleDelete(s)} data-testid={`delete-staff-${s.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[#94A3B8] hover:bg-red-50 hover:text-red-500 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-[#1A1A1A] text-lg">{editingId ? 'Modifica Operatore' : 'Nuovo Operatore'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-[#64748B]"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[#64748B] text-xs font-medium block mb-1">Nome</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Mario Rossi" data-testid="staff-name-input"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-[#64748B] text-xs font-medium block mb-1">Ruolo</label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} data-testid="staff-role-select"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
                  <option value="cashier">Cassiere</option>
                  <option value="supervisor">Supervisore</option>
                  <option value="venue_admin">Admin Punto Vendita</option>
                  <option value="brand_admin">Admin Brand</option>
                </select>
              </div>
              <div>
                <label className="text-[#64748B] text-xs font-medium block mb-1">{editingId ? 'Nuovo PIN (lascia vuoto per non cambiare)' : 'PIN (6 cifre)'}</label>
                <input type="password" value={form.pin} onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/, '').slice(0, 6) }))}
                  placeholder={editingId ? 'Lascia vuoto' : '••••••'} maxLength={6} data-testid="staff-pin-input"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
              </div>

              {/* Venue assignment */}
              {venues.length > 1 && (
                <div>
                  <label className="text-[#64748B] text-xs font-medium block mb-2">Punti vendita assegnati</label>
                  <div className="space-y-1.5" data-testid="staff-venue-selector">
                    {venues.map(v => {
                      const selected = form.venue_ids.includes(v.id);
                      return (
                        <button key={v.id} type="button" onClick={() => toggleVenue(v.id)} data-testid={`staff-venue-${v.id}`}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                            selected ? 'border-[#D44E20] bg-[#FDECE7]' : 'border-slate-100 hover:border-slate-200'
                          }`}>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                            selected ? 'border-[#D44E20] bg-[#D44E20]' : 'border-slate-200'
                          }`}>
                            {selected && <Check size={12} className="text-white" />}
                          </div>
                          <MapPin size={14} className={selected ? 'text-[#D44E20]' : 'text-[#94A3B8]'} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${selected ? 'text-[#D44E20]' : 'text-[#1A1A1A]'}`}>{v.name}</p>
                            {v.address?.full && <p className="text-[10px] text-[#94A3B8] truncate">{v.address.full}</p>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowForm(false)} className="flex-1 h-11 bg-slate-100 text-[#64748B] rounded-xl text-sm font-medium">Annulla</button>
              <button onClick={handleSave} disabled={saving} data-testid="save-staff-btn" className="flex-1 h-11 bg-[#D44E20] text-white rounded-xl text-sm font-semibold disabled:opacity-50">
                {saving ? 'Salvataggio...' : editingId ? 'Salva Modifiche' : 'Aggiungi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
